import React, { useState } from 'react';
import { Input } from './ui/input';
import { Search } from 'lucide-react';

/**
 * Reusable Searchable Dropdown Component
 * Allows users to search and select from a list of options
 */
const SearchableDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Search...",
  getOptionLabel,
  getOptionValue,
  className = "",
  searchKeys = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Get the display value for the selected item
  const selectedOption = options.find(opt => getOptionValue(opt) === value);
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : '';

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const searchLower = searchTerm.toLowerCase();
    
    // Search across all specified keys
    return searchKeys.some(key => {
      const value = option[key];
      return value && value.toString().toLowerCase().includes(searchLower);
    });
  });

  const handleSelect = (option) => {
    onChange(getOptionValue(option));
    setSearchTerm(getOptionLabel(option));
    setShowDropdown(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    
    // Clear selection if search term changes
    if (e.target.value !== displayValue) {
      onChange('');
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-3 h-5 w-5 text-secondary z-10" />
        <Input
          type="text"
          placeholder={placeholder}
          value={showDropdown ? searchTerm : displayValue}
          onChange={handleInputChange}
          onFocus={() => {
            setSearchTerm('');
            setShowDropdown(true);
          }}
          onBlur={() => {
            setTimeout(() => {
              setShowDropdown(false);
              setSearchTerm('');
            }, 200);
          }}
          className="h-12 pl-10 rounded-xl border-2 focus:border-accent"
        />
      </div>
      
      {showDropdown && filteredOptions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-border rounded-xl shadow-lg max-h-60 overflow-y-auto z-50">
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              className="p-4 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 transition-colors"
              onClick={() => handleSelect(option)}
            >
              <div className="font-bold text-primary text-base">
                {getOptionLabel(option)}
              </div>
              {/* Optional secondary info can be added here */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
